// AutopilotExpanded — Omega ZI Autopilot Command Center v3
import { useState, useEffect } from "react";
import axios from "axios";
import { f$, API } from "./_shared";
import { Skel } from "./Skel";
import { fastGet } from "../../lib/fastFetch";
import { hapticTap } from "../../utils/haptics";

// ── Mode definitions ──────────────────────────────────────────────────────────
const MODE_DEFS = [
  { id:"OFF",            label:"Off",        icon:"○",  desc:"No scans, no trades",                        color:"#8e8e93" },
  { id:"OBSERVE_ONLY",   label:"Observe",    icon:"◎",  desc:"Signal only · no execution",                 color:"#60a5fa" },
  { id:"ASSISTED",       label:"Assisted",   icon:"⚑",  desc:"Confirm before each trade · Zi ≥ 65",        color:"#a78bfa" },
  { id:"GEM_ONLY",       label:"Gem Only",   icon:"◆",  desc:"Auto-execute gems · Zi ≥ 85 · Meta ≥ 0.80", color:"#30d158" },
  { id:"DEFENSE",        label:"Defense",    icon:"⬡",  desc:"Exits only · no new entries",                color:"#ff9f0a" },
  { id:"FULL_AUTO",      label:"Full Auto",  icon:"◉",  desc:"All 8 trade types · Zi ≥ 75",                color:"#34c759" },
  { id:"LIQUIDATE_RISK", label:"Liquidate",  icon:"⬇",  desc:"Force-close losing positions immediately",   color:"#FF453A" },
];

const LEVEL_DEFS = [
  {
    id:"basic", label:"BASIC", color:"#8e8e93",
    desc:"Long calls/puts only · 14-45 DTE · 1 contract",
    minZi:88, dte:"14–45 DTE", maxContracts:1, ivMax:"IVR ≤ 50",
    tradeTypes:["Long Call","Long Put"],
    rules:[
      "Long calls & puts only — no spreads",
      "ATM to 30Δ strikes · 1 contract per trade",
      "14–45 DTE window · no earnings plays",
      "Hold to next trading day · exit instantly at +300%",
      "Max 3% account risk · Zi ≥ 88 · Meta ≥ 0.82",
    ],
  },
  {
    id:"pro", label:"PRO", color:"#30d158",
    desc:"+ Spreads & covered calls · 7-60 DTE · 2 contracts",
    minZi:78, dte:"7–60 DTE", maxContracts:2, ivMax:"IVR ≤ 65",
    tradeTypes:["Spreads","Covered Call","Naked Put","Long Options"],
    rules:[
      "All BASIC + vertical debit & credit spreads",
      "Covered calls · naked puts (defined-risk preferred)",
      "7–60 DTE · up to 2 contracts per trade",
      "Hold to next trading day · exit instantly at +300%",
      "Max 5% account risk · Zi ≥ 78 · Meta ≥ 0.75",
    ],
  },
  {
    id:"ultra", label:"ULTRA", color:"#60a5fa",
    desc:"+ Condors, butterflies, calendars · 1-60 DTE · 5 contracts",
    minZi:72, dte:"1–60 DTE", maxContracts:5, ivMax:"IVR ≤ 80",
    tradeTypes:["Condors","Butterflies","Calendars","Straddles","All PRO"],
    rules:[
      "All PRO + iron condors · butterflies · calendars",
      "Straddles · strangles · jade lizards",
      "1–60 DTE · up to 5 contracts · earnings plays allowed",
      "Hold to next trading day · exit instantly at +300%",
      "Max 8% account risk · Zi ≥ 72 · Meta ≥ 0.70",
    ],
  },
  {
    id:"omega", label:"OMEGA", color:"#a78bfa",
    desc:"Full 101-strategy library · 1-180 DTE · 10 contracts",
    minZi:65, dte:"1–180 DTE", maxContracts:10, ivMax:"IVR ≤ 100",
    tradeTypes:["All 101 Strategies","Ratio Spreads","Backspreads","Earnings Plays","Custom"],
    rules:[
      "Complete 101-strategy options library unlocked",
      "Ratio spreads · backspreads · custom combos",
      "1–180 DTE · up to 10 contracts · Kelly ×1.0",
      "Hold to next trading day · exit instantly at +300%",
      "Max 12% account risk · Zi ≥ 65 · widest gates",
    ],
  },
];

const TIER_COLORS = {
  survival: { color:"#fbbf24", bg:"rgba(251,191,36,0.10)", border:"rgba(251,191,36,0.22)" },
  builder:  { color:"#4ade80", bg:"rgba(74,222,128,0.10)", border:"rgba(74,222,128,0.22)" },
  operator: { color:"#38bdf8", bg:"rgba(56,189,248,0.10)", border:"rgba(56,189,248,0.22)" },
  sovereign:{ color:"#a78bfa", bg:"rgba(167,139,250,0.10)", border:"rgba(167,139,250,0.22)" },
};

function relTime(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60)   return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return new Date(ts).toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" });
}

function thinkIcon(msg = "") {
  const m = msg.toLowerCase();
  if (m.includes("buy") || m.includes("long") || m.includes("entry"))  return { i:"↑", c:"#30d158" };
  if (m.includes("sell") || m.includes("exit") || m.includes("close")) return { i:"↓", c:"#FF453A" };
  if (m.includes("skip") || m.includes("pass") || m.includes("reject")) return { i:"—", c:"#8e8e93" };
  if (m.includes("regime") || m.includes("market"))                     return { i:"⚡", c:"#ff9f0a" };
  if (m.includes("kill") || m.includes("alert") || m.includes("warn"))  return { i:"!", c:"#FF453A" };
  if (m.includes("scan") || m.includes("grad"))                         return { i:"◎", c:"#60a5fa" };
  return { i:"Ω", c:"#a78bfa" };
}

// ── Agent identities (mirrors backend AGENTS dict) ──────────────────────────
const AGENT_META = {
  kim:    { name:"Kim",    title:"CIO",                 color:"#a78bfa" },
  marcus: { name:"Marcus", title:"Data Lead",            color:"#60a5fa" },
  zara:   { name:"Zara",   title:"IV / Volatility Analyst", color:"#34c759" },
  dev:    { name:"Dev",    title:"Pricing Analyst",     color:"#ff9f0a" },
  riley:  { name:"Riley",  title:"Momentum Analyst",   color:"#30d158" },
  jade:   { name:"Jade",   title:"Flow Analyst",        color:"#64d2ff" },
  omar:   { name:"Omar",   title:"Catalyst Analyst",   color:"#ffd60a" },
  khalil: { name:"Khalil", title:"Head of Research",   color:"#bf5af2" },
  jordan: { name:"Jordan", title:"Chief Risk Officer",  color:"#ff6b6b" },
  alex:   { name:"Alex",   title:"Trader",              color:"#4ade80" },
  sam:    { name:"Sam",    title:"Ops",                 color:"#8e8e93" },
  omega:  { name:"Omega",  title:"Principal + PM",      color:"#ff9f0a" },
  arthur: { name:"Arthur", title:"Goals Strategist",   color:"#f97316" },
};

const SAFETY_DEFS = [
  { key:"killSwitchDailyLoss",   label:"Daily Loss KS",    desc:"Fires if daily loss exceeds tier cap" },
  { key:"killSwitchDrawdown",    label:"Drawdown KS",       desc:"Fires if drawdown exceeds cap" },
  { key:"killSwitchApiHealth",   label:"API Health KS",     desc:"Fires on API error storms" },
  { key:"killSwitchCalibration", label:"Calibration KS",   desc:"Fires if model accuracy degrades" },
  { key:"riskGateTradeCvr",      label:"Trade CVaR Gate",  desc:"Per-trade capital-at-risk limit" },
  { key:"riskGateMaxPositions",  label:"Position Limit",   desc:"Max concurrent open positions" },
  { key:"riskGateKellySizing",   label:"Kelly Sizing",     desc:"Optimal size calculation gate" },
  { key:"riskGatePdt",           label:"PDT Guard",        desc:"Pattern Day Trader compliance" },
  { key:"liquidityFloor",        label:"Liquidity Floor",  desc:"Min OI + max spread enforcement" },
  { key:"metaConfidenceVeto",    label:"Meta Conf. Veto",  desc:"Requires ≥ 0.65 model agreement" },
  { key:"popFloor",              label:"PoP Floor",        desc:"Min 30% probability of profit" },
];

export function AutopilotExpanded({ pnl }) {
  const [status,        setStatus]        = useState(null);
  const [thinking,      setThinking]      = useState([]);
  const [stats,         setStats]         = useState(null);
  const [autopilotMode, setAutopilotMode] = useState(() => localStorage.getItem("omega_mode") || null);
  const [autonomyLevel, setAutonomyLevel] = useState(() => localStorage.getItem("omega_autonomy_level") || "omega");
  const [saving,        setSaving]        = useState("");
  const [savedFlash,    setSavedFlash]    = useState("");
  const [dailyPnlData,  setDailyPnlData]  = useState(null);
  const [expandedThink, setExpandedThink] = useState(null);
  const [safetyToggles, setSafetyToggles] = useState({});
  const [safetyExpanded,setSafetyExpanded]= useState(false);

  const applyMode = (m) => {
    if (!m) return;
    setAutopilotMode(m);
    localStorage.setItem("omega_mode", m);
  };

  const applyLevel = (l) => {
    if (!l) return;
    const lvl = l.toLowerCase();
    setAutonomyLevel(lvl);
    localStorage.setItem("omega_autonomy_level", lvl);
  };

  const loadAll = () => {
    axios.get(`${API}/omega/status`).then(r => {
      const d = r.data;
      setStatus(d);
      applyLevel(d?.autonomyLevel || "omega");
      const serverMode = d?.autopilotMode || d?.mode;
      if (serverMode) applyMode(serverMode);
    }).catch(() => {});
    axios.get(`${API}/autopilot/thinking`).then(r => setThinking((r.data || []).slice(-20).reverse())).catch(() => {});
    axios.get(`${API}/autopilot/paper-stats`).then(r => setStats(r.data)).catch(() => {});
    fastGet(`${API}/daily-pnl`, setDailyPnlData);
    axios.get(`${API}/omega/safety-toggles`).then(r => setSafetyToggles(r.data || {})).catch(() => {});
  };

  useEffect(() => {
    fastGet(`${API}/omega/status`, d => {
      setStatus(d);
      applyLevel(d?.autonomyLevel || "omega");
      const serverMode = d?.autopilotMode || d?.mode;
      if (serverMode) applyMode(serverMode);
    });
    fastGet(`${API}/autopilot/thinking`, d => setThinking((d || []).slice(-12).reverse()));
    fastGet(`${API}/autopilot/paper-stats`, setStats);
    fastGet(`${API}/daily-pnl`, setDailyPnlData);
    const iv = setInterval(loadAll, 5000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = async (m) => {
    const prev = autopilotMode;
    applyMode(m);  // instant — updates UI and localStorage immediately
    setSaving(`mode:${m}`);
    try {
      await axios.put(`${API}/settings`, { autopilotMode: m });
      setSaving("");
      setSavedFlash(m);
      setTimeout(() => setSavedFlash(""), 1500);
    } catch {
      applyMode(prev);  // revert on failure
      setSaving("");
    }
  };

  const changeAutonomy = async (lvl) => {
    if (saving) return;
    const prev = autonomyLevel;
    applyLevel(lvl);  // optimistic — writes localStorage immediately
    setSaving(`autonomy:${lvl}`);
    try {
      await axios.put(`${API}/omega/autonomy`, { level: lvl });
      setTimeout(() => setSaving(""), 1200);
    } catch {
      applyLevel(prev);  // revert both state and localStorage on failure
      setSaving("");
    }
  };

  const toggleKS = async (num) => {
    try {
      await axios.post(`${API}/omega/kill-switch/manual`, { switch: num });
      loadAll();
    } catch (e) { console.error(e); }
  };

  const toggleSafety = async (key, currentVal) => {
    hapticTap();
    const next = !currentVal;
    setSafetyToggles(prev => ({ ...prev, [key]: next }));
    try {
      await axios.post(`${API}/omega/safety-toggles`, { [key]: next });
    } catch {
      setSafetyToggles(prev => ({ ...prev, [key]: currentVal }));
    }
  };

  const toggleAllSafety = async (disable) => {
    hapticTap();
    setSafetyToggles(prev => ({ ...prev, disableAllSafety: disable }));
    try {
      await axios.post(`${API}/omega/safety-toggles`, { disableAllSafety: disable });
    } catch {
      setSafetyToggles(prev => ({ ...prev, disableAllSafety: !disable }));
    }
  };

  // ── Derived values ──────────────────────────────────────────────────────────
  const ks            = status?.killSwitches?.switches || [];
  // status.equity is already capped by the backend when survival mode is active.
  // status.rawEquity is the real account balance — always present from backend.
  const survivalCap   = status?.survivalCap ?? null;
  const equity        = status?.equity ?? dailyPnlData?.totalEquity ?? pnl?.totalEquity ?? 0;
  const rawEquity     = status?.rawEquity ?? dailyPnlData?.totalEquity ?? pnl?.totalEquity ?? equity;
  const dayPnl        = dailyPnlData?.totalDayPnl ?? pnl?.totalDayPnl ?? 0;
  const dayPnlPct     = dailyPnlData?.dayPnlPct   ?? pnl?.dayPnlPct  ?? 0;
  const isPos         = dayPnl >= 0;
  const ksFired       = status?.killSwitches?.anyFired;
  const apOff         = autopilotMode === "OFF";
  const apRunning     = !apOff && !ksFired;
  const paperPnl      = stats?.totalPnl || stats?.pnl || 0;
  const scanCount     = status?.scanCount || status?.lastScanCount || 0;
  const regimeName    = (status?.regime || stats?.regime || "").replace(/_/g, " ");
  const currentDef    = MODE_DEFS.find(m => m.id === autopilotMode) || MODE_DEFS[0];
  const winRate       = stats?.paperWinRate ?? stats?.winRate ?? null;
  const winRatePct    = winRate != null ? (winRate > 1 ? winRate : winRate * 100) : null;

  // Hero gradient — contextual
  const heroBg = ksFired
    ? "linear-gradient(145deg,#3d0000 0%,#7f0000 100%)"
    : apOff
    ? "linear-gradient(145deg,#111118 0%,#1a1a28 100%)"
    : !isPos
    ? "linear-gradient(145deg,#3d0c00 0%,#7c1d00 100%)"
    : "linear-gradient(145deg,#002910 0%,#003d18 100%)";

  const heroCol = ksFired ? "#FF453A"
    : apOff  ? "#8e8e93"
    : !isPos ? "#ff6b35"
    : "#30d158";

  const heroLabel = ksFired ? "KILL SWITCH ACTIVE"
    : apOff  ? "AUTOPILOT OFF"
    : "OMEGA LIVE";

  // ── Skeleton ────────────────────────────────────────────────────────────────
  if (status === null) {
    return (
      <div style={{ padding:"0 16px 40px" }} data-testid="autopilot-expanded-skel">
        <Skel w="100%" h={238} mb={14} radius={28} />
        <Skel w={140} h={10} mb={10} />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
          {Array.from({length:6}).map((_,i) => <Skel key={i} w="100%" h={64} radius={16} mb={0}/>)}
        </div>
        <Skel w="100%" h={44} mb={16} radius={14} />
        {Array.from({length:3}).map((_,i) => <Skel key={i} w="100%" h={52} radius={14} mb={6}/>)}
      </div>
    );
  }

  return (
    <div style={{ padding:"0 16px 48px" }} data-testid="autopilot-expanded">
      <style>{`
        @keyframes ap-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.35;transform:scale(0.55)} }
      `}</style>

      {/* ── Drift / GEM-ONLY banner ── */}
      {status?.drift?.severity && status.drift.severity !== "none" && status.drift.severity !== "healthy" && (
        <div style={{
          marginBottom:12, padding:"12px 14px", borderRadius:16,
          background:status.drift.severity === "severe"
            ? "linear-gradient(135deg,rgba(120,53,15,0.40),rgba(180,83,9,0.28))"
            : "rgba(96,165,250,0.07)",
          border:`1px solid ${status.drift.severity === "severe" ? "rgba(251,191,36,0.50)" : "rgba(96,165,250,0.28)"}`,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ fontSize:9, fontWeight:900, letterSpacing:1.8, color:"#fbbf24", textTransform:"uppercase", marginBottom:4 }}>
              ⚠ {status.drift.severity === "severe" ? "GEM-ONLY DRIFT OVERRIDE" : `DRIFT · ${String(status.drift.severity).toUpperCase()}`}
            </div>
            {status.drift.severity === "severe" && (
              <div style={{ fontSize:8, fontWeight:700, color:"rgba(251,191,36,0.55)", letterSpacing:0.8, textAlign:"right", lineHeight:1.3 }}>
                OVERRIDES<br/>MODE SELECTION
              </div>
            )}
          </div>
          <div style={{ fontSize:12, color:"#fef3c7", lineHeight:1.5 }}>
            Win rate <strong>{status.drift.recentWinRate != null ? `${status.drift.recentWinRate}%` : "—"}</strong> vs 80% baseline.{" "}
            {status.drift.severity === "severe"
              ? "Omega auto-raised the bar: Zi ≥ 85 + Meta ≥ 0.80, ½ size. Clears when win rate recovers."
              : status.drift.severity === "persistent" ? "Threshold +10 pts, sizes ×0.75." : "Threshold +5 pts."}
          </div>
        </div>
      )}

      {/* ═══ HERO CARD ═══ */}
      <div style={{
        borderRadius:28, padding:"22px 20px 20px", marginBottom:18,
        background:heroBg, position:"relative", overflow:"hidden",
        border:`1px solid ${heroCol}24`,
        boxShadow:`0 16px 40px ${heroCol}1A, inset 0 1px 0 rgba(255,255,255,0.06)`,
        transition:"background 600ms ease",
      }}>
        {/* Ambient glow blob */}
        <div style={{ position:"absolute", top:-60, right:-40, width:220, height:220, borderRadius:"50%", background:`${heroCol}16`, filter:"blur(64px)", pointerEvents:"none" }}/>

        {/* Top row: live status + active mode badge */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, position:"relative" }}>
          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
            <span style={{
              width:7, height:7, borderRadius:"50%",
              background:heroCol, boxShadow:`0 0 10px ${heroCol}`,
              animation:apRunning ? "ap-pulse 2s infinite" : "none",
              flexShrink:0, display:"inline-block",
            }}/>
            <span style={{ fontSize:9, fontWeight:800, color:heroCol, letterSpacing:2.2, textTransform:"uppercase" }}>{heroLabel}</span>
          </div>
          {autopilotMode && (
            <span style={{
              fontSize:9, fontWeight:800, padding:"4px 11px", borderRadius:20,
              background:`${currentDef.color}1E`, border:`1px solid ${currentDef.color}40`,
              color:currentDef.color, letterSpacing:1, textTransform:"uppercase",
            }}>
              {currentDef.label}
            </span>
          )}
        </div>

        {/* Headline */}
        <div style={{ fontSize:24, fontWeight:800, color:"#fff", letterSpacing:-0.5, lineHeight:1.15, marginBottom:6 }}>
          {ksFired ? "Trading Paused — Risk Gate Active"
           : apOff  ? "Autopilot Offline"
           : "Scanning & Grading Setups"}
        </div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.55)", marginBottom:14, display:"flex", flexWrap:"wrap", gap:"0 6px" }}>
          <span>Autonomy · <strong style={{ color:"rgba(255,255,255,0.85)" }}>{autonomyLevel.toUpperCase()}</strong></span>
          {scanCount > 0 && <span>· <strong style={{ color:"rgba(255,255,255,0.85)" }}>{scanCount}</strong> tickers</span>}
          {regimeName && <span>· <strong style={{ color:heroCol }}>{regimeName}</strong></span>}
        </div>

        {/* Stats row */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
          {[
            { label:"Equity",   val:f$(equity),                                             col:"#fff" },
            { label:"Day P&L",  val:`${isPos?"+":"-"}${f$(Math.abs(dayPnl))} (${isPos?"+":""}${dayPnlPct.toFixed(1)}%)`, col:isPos?"#86efac":"#fca5a5" },
            { label:"Win Rate", val:winRatePct != null ? `${winRatePct.toFixed(0)}%` : "—", col:winRatePct >= 60 ? "#86efac" : winRatePct != null ? "#fca5a5" : "rgba(255,255,255,0.55)" },
          ].map(s => (
            <div key={s.label} style={{ background:"rgba(0,0,0,0.22)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"10px 10px 9px" }}>
              <div style={{ fontSize:8, color:"rgba(255,255,255,0.42)", letterSpacing:1.2, textTransform:"uppercase", marginBottom:3 }}>{s.label}</div>
              <div style={{ fontSize:13, fontWeight:800, color:s.col, fontVariantNumeric:"tabular-nums", lineHeight:1.2 }}>{s.val}</div>
            </div>
          ))}
        </div>

        {/* Paper ledger line */}
        {paperPnl !== 0 && (
          <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:8, fontWeight:800, color:"rgba(255,255,255,0.28)", letterSpacing:1.2, textTransform:"uppercase" }}>Paper</span>
            <span style={{ fontSize:11, fontWeight:700, color:paperPnl >= 0 ? "#86efac" : "#fca5a5" }}>
              {paperPnl >= 0 ? "+" : "-"}{f$(Math.abs(paperPnl))}
            </span>
            <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)" }}>simulation ledger</span>
          </div>
        )}
      </div>

      {/* ═══ AUTOPILOT MODE SELECTOR ═══ */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:1.5, color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>Autopilot Mode</span>
          {savedFlash && <span style={{ fontSize:9, fontWeight:700, color:"#30d158" }}>✓ Saved</span>}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {MODE_DEFS.map(m => {
            const active    = autopilotMode === m.id;
            const isSaving  = saving === `mode:${m.id}`;
            const justSaved = savedFlash === m.id;
            return (
              <button key={m.id} onClick={() => { hapticTap(); changeMode(m.id); }}
                data-testid={`autopilot-mode-${m.id}`}
                style={{
                  padding:"13px 12px 11px", borderRadius:18, textAlign:"left", cursor:"pointer",
                  background:active ? `${m.color}22` : "rgba(255,255,255,0.025)",
                  border:`1.5px solid ${active ? m.color : "rgba(255,255,255,0.06)"}`,
                  boxShadow:active ? `0 0 22px ${m.color}30, inset 0 1px 0 ${m.color}18` : "none",
                  transition:"all 180ms ease", fontFamily:"inherit", position:"relative",
                  WebkitTapHighlightColor:"transparent",
                  outline: isSaving ? `2px solid ${m.color}55` : "none",
                }}
              >
                <span style={{
                  position:"absolute", top:9, right:10, fontSize:8, fontWeight:900,
                  letterSpacing:0.8, textTransform:"uppercase",
                  color: active ? m.color : "transparent",
                }}>
                  {isSaving ? "···" : justSaved ? "✓ SAVED" : active ? "ACTIVE" : ""}
                </span>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
                  <span style={{ fontSize:15, color:active ? m.color : "rgba(255,255,255,0.25)" }}>{m.icon}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:active ? m.color : "rgba(255,255,255,0.78)", letterSpacing:0.2 }}>{m.label}</span>
                </div>
                <div style={{ fontSize:9.5, color:"rgba(255,255,255,0.33)", lineHeight:1.45 }}>{m.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ AUTONOMY LEVEL ═══ */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:1.5, color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>Strategy Access · Autonomy</span>
          {saving.startsWith("autonomy:") && <span style={{ fontSize:9, fontWeight:700, color:"#30d158" }}>✓ Saved</span>}
        </div>
        <div style={{ display:"flex", gap:3, background:"rgba(255,255,255,0.04)", borderRadius:15, padding:"3px" }}>
          {LEVEL_DEFS.map(l => {
            const active = autonomyLevel === l.id;
            return (
              <button key={l.id} onClick={() => { hapticTap(); changeAutonomy(l.id); }}
                data-testid={`mobile-autonomy-${l.id}`}
                style={{
                  flex:1, padding:"10px 0", textAlign:"center", borderRadius:12, border:"none",
                  background:active ? `${l.color}1E` : "transparent",
                  color:active ? l.color : "rgba(255,255,255,0.28)",
                  fontSize:10, fontWeight:active ? 800 : 500, cursor:"pointer",
                  fontFamily:"inherit", letterSpacing:0.5,
                  transition:"all 180ms ease", WebkitTapHighlightColor:"transparent",
                }}
              >{l.label}</button>
            );
          })}
        </div>
        <div style={{ fontSize:10, color:"rgba(255,255,255,0.30)", marginTop:7, textAlign:"center" }}>
          {LEVEL_DEFS.find(l => l.id === autonomyLevel)?.desc}
        </div>
      </div>

      {/* ═══ TIER INTELLIGENCE ═══ */}
      {(() => {
        const ct = status?.capitalTier;
        const st = status?.scannerTier;
        const nt = status?.nextTier;
        if (!ct) return null;
        const tc = TIER_COLORS[ct.id] || TIER_COLORS.survival;
        const progressPct = nt ? Math.min(100, Math.round((equity / nt.minEquity) * 100)) : 100;
        const rules = [
          { label:"Risk / Trade",   val:`${ct.max_risk_pct}%`,   note:"Max loss per position" },
          { label:"Max Positions",  val:`${ct.max_positions}`,    note:"Concurrent cap" },
          { label:"Daily Loss Cap", val:`${ct.daily_loss_cap}%`,  note:"Kill switch threshold" },
          { label:"Kelly Fraction", val:`${ct.kelly_frac}×`,      note:"Position size mult." },
        ];
        const currentLvl = LEVEL_DEFS.find(l => l.id === autonomyLevel) || LEVEL_DEFS[3];
        return (
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.5, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", marginBottom:10 }}>Tier Intelligence</div>

            {/* Survival cap banner */}
            {survivalCap != null && (
              <div style={{
                marginBottom:10, padding:"9px 14px", borderRadius:14,
                background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.28)",
                display:"flex", alignItems:"center", justifyContent:"space-between",
              }}>
                <div>
                  <div style={{ fontSize:8, fontWeight:900, letterSpacing:1.6, color:"#fbbf24", textTransform:"uppercase", marginBottom:1 }}>Survival Cap Active</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", lineHeight:1.4 }}>
                    Effective equity locked at <strong style={{ color:"#fbbf24" }}>{f$(survivalCap)}</strong>
                    {rawEquity > survivalCap && <span style={{ color:"rgba(255,255,255,0.30)" }}> · real {f$(rawEquity)}</span>}
                  </div>
                </div>
                <span style={{ fontSize:18 }}>🛡</span>
              </div>
            )}

            {/* Tier header card */}
            <div style={{ borderRadius:20, padding:"16px", background:tc.bg, border:`1px solid ${tc.border}`, marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.4, color:tc.color, textTransform:"uppercase", marginBottom:3 }}>Capital Tier</div>
                  <div style={{ fontSize:22, fontWeight:900, color:"#fff", letterSpacing:-0.5 }}>{ct.label}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", marginBottom:2 }}>
                    {survivalCap != null ? "Effective equity" : "Account equity"}
                  </div>
                  <div style={{ fontSize:18, fontWeight:800, color:survivalCap != null ? "#fbbf24" : "#fff" }}>{f$(equity)}</div>
                  {survivalCap != null && rawEquity > survivalCap && (
                    <div style={{ fontSize:9, color:"rgba(255,255,255,0.28)", marginTop:1 }}>real {f$(rawEquity)}</div>
                  )}
                </div>
              </div>
              {nt ? (
                <div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:9, color:"rgba(255,255,255,0.38)", marginBottom:5 }}>
                    <span>{ct.label}</span>
                    <span style={{ color:tc.color }}>{f$(nt.needed)} to {nt.label}</span>
                  </div>
                  <div style={{ height:5, borderRadius:3, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                    <div style={{ height:"100%", borderRadius:3, width:`${progressPct}%`, background:`linear-gradient(90deg,${tc.color}66,${tc.color})`, transition:"width 700ms ease" }}/>
                  </div>
                  <div style={{ fontSize:8, color:"rgba(255,255,255,0.26)", marginTop:4, textAlign:"right" }}>{progressPct}% to {nt.label}</div>
                </div>
              ) : (
                <div style={{ fontSize:10, color:tc.color, fontWeight:700 }}>✦ Maximum tier — Sovereign</div>
              )}
            </div>

            {/* Risk rules */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:10 }}>
              {rules.map((r, i) => (
                <div key={i} style={{ padding:"10px 12px", borderRadius:12, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ fontSize:8, color:"rgba(255,255,255,0.30)", marginBottom:3 }}>{r.label}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:tc.color }}>{r.val}</div>
                  <div style={{ fontSize:8, color:"rgba(255,255,255,0.20)", marginTop:1 }}>{r.note}</div>
                </div>
              ))}
            </div>

            {/* Level Intelligence — options rules for active autonomy level */}
            <div style={{ borderRadius:16, padding:"14px", background:`${currentLvl.color}0D`, border:`1px solid ${currentLvl.color}28`, marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:8, fontWeight:900, letterSpacing:1.6, color:currentLvl.color, textTransform:"uppercase", marginBottom:2 }}>Level Intelligence · {currentLvl.label}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)" }}>{currentLvl.dte} · {currentLvl.maxContracts} contract{currentLvl.maxContracts > 1 ? "s" : ""} max · {currentLvl.ivMax}</div>
                </div>
                <div style={{ fontSize:9, fontWeight:800, padding:"4px 10px", borderRadius:20, background:`${currentLvl.color}1E`, color:currentLvl.color, border:`1px solid ${currentLvl.color}40` }}>Zi ≥ {currentLvl.minZi}</div>
              </div>
              <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:10 }}>
                {currentLvl.tradeTypes.map(t => (
                  <span key={t} style={{ fontSize:8, fontWeight:700, padding:"2px 8px", borderRadius:6, background:`${currentLvl.color}14`, color:currentLvl.color, border:`1px solid ${currentLvl.color}24` }}>{t}</span>
                ))}
              </div>
              {currentLvl.rules.map((r, i) => (
                <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:6, marginBottom:5 }}>
                  <span style={{ fontSize:8, color:currentLvl.color, flexShrink:0, marginTop:1.5 }}>▸</span>
                  <span style={{ fontSize:10, color:"rgba(255,255,255,0.52)", lineHeight:1.45 }}>{r}</span>
                </div>
              ))}
            </div>

            {/* Scanner universe */}
            {st && (
              <div style={{ borderRadius:14, padding:"12px 14px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                  <div>
                    <div style={{ fontSize:9, fontWeight:700, letterSpacing:1, color:"rgba(255,255,255,0.32)", textTransform:"uppercase", marginBottom:2 }}>Scanner Universe</div>
                    <div style={{ fontSize:13, fontWeight:800, color:"#fff" }}>{st.label} · {st.tickers?.length || 0} tickers</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:8, color:"rgba(255,255,255,0.30)" }}>Max cost</div>
                    <div style={{ fontSize:12, fontWeight:700, color:"#fff" }}>{f$(st.maxCostPerContract)}/contract</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                  {(st.tickers || []).slice(0, 12).map(t => (
                    <span key={t} style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:5, background:"rgba(96,165,250,0.10)", color:"#60a5fa", border:"1px solid rgba(96,165,250,0.18)" }}>{t}</span>
                  ))}
                  {(st.tickers?.length || 0) > 12 && <span style={{ fontSize:9, color:"rgba(255,255,255,0.28)", padding:"2px 4px" }}>+{st.tickers.length - 12} more</span>}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ SAFETY CONTROLS ═══ */}
      <div style={{ marginBottom:20 }}>
        {/* Header row */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, cursor:"pointer" }}
          onClick={() => setSafetyExpanded(p => !p)}>
          <div>
            <div style={{ fontSize:9, fontWeight:800, letterSpacing:1.5, color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>Safety Controls · Kill Switches</div>
            {safetyToggles.disableAllSafety && (
              <div style={{ fontSize:9, fontWeight:800, color:"#FF453A", letterSpacing:0.8, marginTop:2 }}>⚠ ZERO SAFETY MODE ACTIVE</div>
            )}
          </div>
          <span style={{ fontSize:12, color:"rgba(255,255,255,0.28)" }}>{safetyExpanded ? "▲" : "▼"}</span>
        </div>

        {/* Kill switch status indicators (always visible) */}
        {ks.length > 0 && (
          <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
            {ks.map(sw => (
              <div key={sw.switch}
                data-testid={`kill-switch-${sw.switch}`}
                onClick={() => sw.switch === 5 && toggleKS(5)}
                style={{
                  padding:"5px 10px", borderRadius:8, fontSize:9, fontWeight:700,
                  background:sw.fired ? "rgba(255,69,58,0.14)" : "rgba(48,209,88,0.08)",
                  border:`1px solid ${sw.fired ? "rgba(255,69,58,0.40)" : "rgba(48,209,88,0.20)"}`,
                  color:sw.fired ? "#ff6b6b" : "rgba(255,255,255,0.45)",
                  cursor:sw.switch === 5 ? "pointer" : "default",
                }}>
                {sw.fired ? "🔴" : "🟢"} {sw.name}
              </div>
            ))}
          </div>
        )}

        {/* Expandable toggles panel */}
        {safetyExpanded && (
          <div style={{ borderRadius:18, background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.07)", padding:"14px 14px 10px", marginBottom:10 }}>

            {/* Nuclear option — Zero Safety Mode */}
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"12px 14px", borderRadius:14, marginBottom:12,
              background:safetyToggles.disableAllSafety ? "rgba(255,69,58,0.12)" : "rgba(255,255,255,0.03)",
              border:`1.5px solid ${safetyToggles.disableAllSafety ? "rgba(255,69,58,0.50)" : "rgba(255,69,58,0.20)"}`,
            }}>
              <div>
                <div style={{ fontSize:12, fontWeight:800, color:"#FF453A", marginBottom:3 }}>⚡ Zero Safety Mode</div>
                <div style={{ fontSize:9.5, color:"rgba(255,255,255,0.38)", lineHeight:1.4 }}>
                  Bypasses ALL gates. Omega has full freedom — no kill switches, no risk gate, no PoP floor. Use with extreme care.
                </div>
              </div>
              <div onClick={() => toggleAllSafety(!safetyToggles.disableAllSafety)}
                style={{
                  width:46, height:26, borderRadius:13, flexShrink:0, marginLeft:14, position:"relative", cursor:"pointer",
                  background:safetyToggles.disableAllSafety ? "#FF453A" : "rgba(255,255,255,0.10)",
                  border:`1px solid ${safetyToggles.disableAllSafety ? "#FF453A" : "rgba(255,255,255,0.18)"}`,
                  transition:"background 220ms",
                }}>
                <div style={{ position:"absolute", top:3, left:safetyToggles.disableAllSafety ? 22 : 3, width:20, height:20, borderRadius:"50%", background:"#fff", transition:"left 220ms ease", boxShadow:"0 1px 4px rgba(0,0,0,0.4)" }}/>
              </div>
            </div>

            {/* Individual feature toggles */}
            <div style={{ fontSize:8, fontWeight:800, letterSpacing:1.2, color:"rgba(255,255,255,0.22)", textTransform:"uppercase", marginBottom:8 }}>
              Individual Safety Features
            </div>
            {SAFETY_DEFS.map(({ key, label, desc }) => {
              const on = safetyToggles[key] !== false;
              const dim = safetyToggles.disableAllSafety;
              return (
                <div key={key}
                  onClick={() => !dim && toggleSafety(key, on)}
                  style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"10px 4px", borderBottom:"1px solid rgba(255,255,255,0.04)",
                    cursor:dim ? "not-allowed" : "pointer", opacity:dim ? 0.4 : 1,
                    transition:"opacity 200ms",
                  }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.80)", marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:9, color:"rgba(255,255,255,0.30)" }}>{desc}</div>
                  </div>
                  {/* Toggle */}
                  <div style={{
                    width:42, height:24, borderRadius:12, flexShrink:0, marginLeft:12, position:"relative",
                    background:on ? "rgba(48,209,88,0.35)" : "rgba(255,255,255,0.10)",
                    border:`1px solid ${on ? "rgba(48,209,88,0.55)" : "rgba(255,255,255,0.15)"}`,
                    transition:"background 200ms",
                  }}>
                    <div style={{ position:"absolute", top:3, left:on ? 20 : 3, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left 200ms ease", boxShadow:"0 1px 4px rgba(0,0,0,0.35)" }}/>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ LIVE AI THINKING · AGENT CHAT FEED ═══ */}
      <div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div>
            <span style={{ fontSize:9, fontWeight:800, letterSpacing:1.5, color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>Agent Brain · Live Chat</span>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.22)", marginTop:2 }}>Kim, Marcus, Khalil & team · thinking together</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ width:5, height:5, borderRadius:"50%", background:"#a78bfa", animation:"ap-pulse 2s infinite", display:"inline-block" }}/>
            <span style={{ fontSize:9, fontWeight:700, color:"#a78bfa", letterSpacing:1 }}>LIVE</span>
          </div>
        </div>

        {thinking.length === 0 ? (
          <div style={{ textAlign:"center", padding:"28px 0", color:"rgba(255,255,255,0.22)", fontSize:12 }}>
            No agent activity yet · warming up
          </div>
        ) : (
          thinking.map((t, i) => {
            const agentId = t.agentId || "";
            const agentMeta = AGENT_META[agentId] || null;
            const agentColor = agentMeta?.color || "#8e8e93";
            const agentName  = agentMeta?.name  || t.agent || "";
            const agentTitle = agentMeta?.title || t.agentTitle || "";
            const isExp = expandedThink === i;
            const isToMe = t.to && AGENT_META[t.to];
            const toMeta = isToMe ? AGENT_META[t.to] : null;

            return (
              <div key={i}
                onClick={() => setExpandedThink(isExp ? null : i)}
                style={{
                  padding:"10px 12px", borderRadius:16, marginBottom:8, cursor:"pointer",
                  background:isExp ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.022)",
                  border:`1px solid ${isExp ? `${agentColor}22` : "rgba(255,255,255,0.05)"}`,
                  transition:"background 150ms, border-color 150ms",
                  WebkitTapHighlightColor:"transparent",
                }}
              >
                <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                  {/* Agent avatar circle */}
                  {agentName ? (
                    <div style={{
                      width:30, height:30, borderRadius:15, flexShrink:0,
                      background:`${agentColor}20`, border:`1.5px solid ${agentColor}50`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:10, fontWeight:900, color:agentColor, marginTop:1,
                      letterSpacing:0,
                    }}>
                      {agentName[0]}
                    </div>
                  ) : (
                    <div style={{ width:30, height:30, borderRadius:15, flexShrink:0, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.10)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#a78bfa", marginTop:1 }}>
                      Ω
                    </div>
                  )}

                  <div style={{ flex:1, minWidth:0 }}>
                    {/* Name row */}
                    <div style={{ display:"flex", alignItems:"center", flexWrap:"wrap", gap:4, marginBottom:4 }}>
                      {agentName && (
                        <span style={{ fontSize:11, fontWeight:800, color:agentColor, letterSpacing:0.2 }}>{agentName}</span>
                      )}
                      {agentTitle && (
                        <span style={{ fontSize:8, fontWeight:600, color:"rgba(255,255,255,0.28)" }}>{agentTitle}</span>
                      )}
                      {toMeta && (
                        <span style={{ fontSize:8, fontWeight:700, padding:"1px 5px", borderRadius:3, background:`${toMeta.color}18`, color:toMeta.color }}>→ {toMeta.name}</span>
                      )}
                      {t.ticker && (
                        <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:5, background:"rgba(96,165,250,0.14)", color:"#60a5fa" }}>{t.ticker}</span>
                      )}
                    </div>
                    {/* Message */}
                    <div style={{
                      fontSize:11.5, color:isExp ? "rgba(255,255,255,0.80)" : "rgba(255,255,255,0.52)",
                      lineHeight:1.55, whiteSpace:"pre-line",
                      overflow:"hidden", display:"-webkit-box",
                      WebkitLineClamp:isExp ? 999 : 3, WebkitBoxOrient:"vertical",
                    }}>
                      {t.message}
                    </div>
                    {/* Education note (expanded) */}
                    {isExp && t.education && (
                      <div style={{ marginTop:8, padding:"8px 10px", borderRadius:10, background:"rgba(167,139,250,0.08)", border:"1px solid rgba(167,139,250,0.16)", fontSize:10, color:"rgba(255,255,255,0.45)", lineHeight:1.5 }}>
                        📖 {t.education}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize:9, color:"rgba(255,255,255,0.20)", flexShrink:0, paddingTop:2 }}>{relTime(t.ts)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
